import * as React from 'react';
import { Button, Input, Spinner } from '@fluentui/react-components';
import { ChevronRightRegular, DocumentRegular, EditRegular, FolderArrowRightRegular, FolderRegular, OpenRegular } from '@fluentui/react-icons';
import type { IBatchResult } from '../../../src/models/Operation';
import { moveFolder, renameFolder, undoBatch } from '../structure/rename';
import MoveFolderDialog from './MoveFolderDialog';
import { errorText, useApp } from '../app/context';
import { getRoot, type IItem, listChildren } from '../graphx';
import { type IClient, normalizeName } from '../../api/src/filing/clients';

/** Which confirmed client a top-level folder belongs to (by its name or an alias), if any. */
function clientOfFolder(name: string, clients: IClient[]): IClient | undefined {
  const n: string = normalizeName(name);
  return clients.filter(c => [c.name, ...(c.aliases ?? [])].some(x => normalizeName(x) === n))[0];
}

const SET_ASIDE: RegExp = /^(set aside by ai admin|set aside|_quarantine)$/i;
const PAGE: number = 50;

interface ICrumb {
  id: string;
  name: string;
  /** The folder's page in SharePoint. */
  webUrl?: string;
}

/** Browse the library as it is now: folders first, labelled by what they mean to AI Admin. */
export default function FolderExplorer(props: { title?: string } = {}): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const [trail, setTrail] = React.useState<ICrumb[] | undefined>(undefined);
  const [items, setItems] = React.useState<IItem[] | undefined>(undefined);
  const [shown, setShown] = React.useState<number>(PAGE);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const here: ICrumb | undefined = trail?.[trail.length - 1];
  const [renaming, setRenaming] = React.useState<{ id: string; name: string } | undefined>(undefined);
  const [working, setWorking] = React.useState<boolean>(false);
  const [reload, setReload] = React.useState<number>(0);

  const [moving, setMoving] = React.useState<IItem | undefined>(undefined);

  const pathOf = (item: IItem): string => [...(trail ?? []).slice(1).map(c => c.name), item.name].join('/');

  /** Runs one folder change, then offers Undo in a toast. */
  const change = async (work: () => Promise<IBatchResult>, done: string): Promise<boolean> => {
    setWorking(true);
    setError(undefined);
    try {
      const batch: IBatchResult = await work();
      setReload(n => n + 1);
      app.toast({
        text: done,
        action: { label: 'Undo', run: () => {
          undoBatch(app.graph, w, app.audit, app.account.username, batch.batchId)
            .then(() => setReload(n => n + 1))
            .catch(e => setError(errorText(e)));
        } }
      });
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setWorking(false);
    }
  };

  const doRename = async (item: IItem, newName: string): Promise<void> => {
    if (!trail || !newName.trim() || newName.trim() === item.name) {
      setRenaming(undefined);
      return;
    }
    const name: string = newName.trim();
    if (await change(() => renameFolder(app.graph, w, app.audit, app.account.username,
      { id: item.id, name: item.name, parentId: here!.id, path: pathOf(item) }, trail[0].id, name), `Renamed "${item.name}" to "${name}".`)) {
      setRenaming(undefined);
    }
  };

  const doMove = async (item: IItem, parentPath: string, label: string): Promise<void> => {
    if (!trail) {
      return;
    }
    if (await change(() => moveFolder(app.graph, w, app.audit, app.account.username,
      { id: item.id, name: item.name, parentId: here!.id, path: pathOf(item) }, trail[0].id, parentPath),
    `Moved "${item.name}" ${parentPath ? `into ${label}` : 'to the top of the library'}.`)) {
      setMoving(undefined);
    }
  };

  React.useEffect(() => {
    getRoot(app.graph, w.driveId)
      .then(root => setTrail([{ id: root.id, name: w.libraryName, webUrl: w.webUrl || root.webUrl }]))
      .catch(e => setError(errorText(e)));
  }, [app.graph, w.driveId, w.libraryName]);

  React.useEffect(() => {
    if (!here) {
      return undefined;
    }
    let cancelled: boolean = false;
    setItems(undefined);
    setShown(PAGE);
    listChildren(app.graph, w.driveId, here.id, 2000)
      .then(list => !cancelled && setItems(list.sort((a, b) => (a.isFolder === b.isFolder ? a.name.localeCompare(b.name) : a.isFolder ? -1 : 1))))
      .catch(e => !cancelled && setError(errorText(e)));
    return () => { cancelled = true; };
  }, [app.graph, w.driveId, here?.id, reload]);

  const atTop: boolean = trail?.length === 1;
  const label = (item: IItem): { text: string; cls: string } | undefined => {
    if (!item.isFolder) {
      return undefined;
    }
    if (atTop && item.name.toLowerCase() === w.inboxName.toLowerCase()) {
      return { text: 'Inbox: new files go here', cls: 'chip' };
    }
    if (atTop && SET_ASIDE.test(item.name)) {
      return { text: 'Set aside, not deleted', cls: 'tag-warn' };
    }
    if (atTop && w.excludedFolders.some(f => f.toLowerCase() === item.name.toLowerCase())) {
      return { text: 'Left alone', cls: 'tag-grey' };
    }
    if (atTop && clientOfFolder(item.name, w.clients)) {
      return { text: 'Client', cls: 'tag-new' };
    }
    return undefined;
  };

  const folders: number = (items ?? []).filter(i => i.isFolder).length;
  const files: number = (items ?? []).length - folders;

  return (
    <section className="card" aria-labelledby="explore-h" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
        <div>
          <h2 id="explore-h">{props.title ?? 'Your library'}</h2>
          <nav aria-label="Folder path" className="crumbs">
            {trail?.map((c, i) => (
              <React.Fragment key={c.id}>
                {i > 0 && <ChevronRightRegular fontSize={12} aria-hidden="true" />}
                {i < trail.length - 1
                  ? <button type="button" className="link-btn" onClick={() => setTrail(trail.slice(0, i + 1))}>{c.name}</button>
                  : <span aria-current="location" style={{ fontWeight: 600 }}>{c.name}</span>}
              </React.Fragment>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {items && <span className="small">{folders} folder{folders === 1 ? '' : 's'} · {files} file{files === 1 ? '' : 's'}</span>}
          {here?.webUrl && (
            <a href={here.webUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
              Open {trail && trail.length > 1 ? 'this folder' : 'library'} in SharePoint<OpenRegular fontSize={14} aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      {error && <p className="small" style={{ padding: '12px 18px', color: 'var(--red)' }}>{error}</p>}
      {!items && !error && <div style={{ padding: 18 }}><Spinner size="small" label="Opening…" labelPosition="after" /></div>}
      {items && items.length === 0 && <p className="small" style={{ padding: '12px 18px' }}>This folder is empty.</p>}
      {items && items.length > 0 && (
        <ul className="explorer">
          {items.slice(0, shown).map(item => {
            const tag = label(item);
            return (
              <li key={item.id}>
                {item.isFolder && renaming?.id === item.id
                  ? (
                    <form className="explorer-row" onSubmit={e => { e.preventDefault(); void doRename(item, renaming.name); }}>
                      <FolderRegular fontSize={18} color="var(--muted)" />
                      <Input aria-label={`New name for ${item.name}`} value={renaming.name} autoFocus disabled={working}
                        onChange={(_, d) => setRenaming({ id: item.id, name: d.value })} style={{ flexGrow: 1 }} />
                      <Button type="submit" appearance="primary" size="small" disabled={working || !renaming.name.trim()}>{working ? 'Renaming…' : 'Rename'}</Button>
                      <Button size="small" disabled={working} onClick={() => setRenaming(undefined)}>Cancel</Button>
                    </form>
                  )
                  : item.isFolder
                  ? (
                    <button type="button" className="explorer-row" onClick={() => setTrail([...(trail ?? []), { id: item.id, name: item.name, webUrl: item.webUrl }])}>
                      <FolderRegular fontSize={18} color="var(--muted)" />
                      <span className="explorer-name">{item.name}</span>
                      {tag && <span className={tag.cls}>{tag.text}</span>}
                      <span className="small explorer-meta">{item.childCount ?? 0} item{item.childCount === 1 ? '' : 's'}</span>
                      {!(atTop && (item.name.toLowerCase() === w.inboxName.toLowerCase() || SET_ASIDE.test(item.name))) && (
                        <span role="button" tabIndex={0} className="icon-btn" aria-label={`Rename ${item.name}`} title="Rename"
                          onClick={e => { e.stopPropagation(); setRenaming({ id: item.id, name: item.name }); }}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setRenaming({ id: item.id, name: item.name }); } }}>
                          <EditRegular fontSize={16} />
                        </span>
                      )}
                      {!(atTop && (item.name.toLowerCase() === w.inboxName.toLowerCase() || SET_ASIDE.test(item.name))) && (
                        <span role="button" tabIndex={0} className="icon-btn" aria-label={`Move ${item.name} to another folder`} title="Move to…"
                          onClick={e => { e.stopPropagation(); setMoving(item); }}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setMoving(item); } }}>
                          <FolderArrowRightRegular fontSize={16} />
                        </span>
                      )}
                      <ChevronRightRegular fontSize={16} aria-hidden="true" />
                    </button>
                  )
                  : (
                    <a className="explorer-row" href={item.webUrl} target="_blank" rel="noreferrer">
                      <DocumentRegular fontSize={18} color="var(--muted)" />
                      <span className="explorer-name mono">{item.name}</span>
                      <span className="small explorer-meta">{item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : ''}</span>
                      <OpenRegular fontSize={16} aria-label="Opens in SharePoint" />
                    </a>
                  )}
              </li>
            );
          })}
        </ul>
      )}
      {items && items.length > shown && (
        <div style={{ padding: '10px 18px' }}><Button size="small" onClick={() => setShown(shown + PAGE)}>Show {Math.min(PAGE, items.length - shown)} more</Button></div>
      )}
      {moving && trail && (
        <MoveFolderDialog folder={{ name: moving.name, path: pathOf(moving) }} root={trail[0]} working={working}
          onMove={(parentPath, label) => { void doMove(moving, parentPath, label); }} onCancel={() => setMoving(undefined)} />
      )}
    </section>
  );
}
